import React, { useState } from 'react';
import { SearchIcon, FilterIcon, EyeIcon, CalendarIcon, AlertTriangleIcon, FlagIcon, ClockIcon, CheckCircleIcon, XCircleIcon, DownloadIcon, RefreshCwIcon, UserIcon, CreditCardIcon, BuildingIcon, ArrowRightIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import StatusBadge from '../../components/ui/StatusBadge';
import ExportMenu from '../../components/ui/ExportMenu';
interface Transaction {
  id: string;
  merchantName: string;
  merchantId: string;
  customerName: string | null;
  customerId: string | null;
  amount: number;
  date: string;
  paymentMethod: string;
  status: 'completed' | 'pending' | 'failed' | 'refunded';
  flagged: boolean;
  ruleTriggered: string | null;
  riskScore: number | null;
}
const TransactionMonitoring: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [flaggedFilter, setFlaggedFilter] = useState<string>('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  // Mock data for transactions
  const transactionsData: Transaction[] = [{
    id: 'TX987654',
    merchantName: 'Global Electronics',
    merchantId: 'M12345',
    customerName: 'John Smith',
    customerId: 'C54321',
    amount: 25000,
    date: '2023-10-28T10:30:00Z',
    paymentMethod: 'Credit Card',
    status: 'completed',
    flagged: true,
    ruleTriggered: 'High Transaction Volume',
    riskScore: 85
  }, {
    id: 'TX987655',
    merchantName: 'City Grocers',
    merchantId: 'M12346',
    customerName: 'Sarah Johnson',
    customerId: 'C54322',
    amount: 12500,
    date: '2023-10-28T09:45:00Z',
    paymentMethod: 'Debit Card',
    status: 'completed',
    flagged: true,
    ruleTriggered: 'Same Customer Multiple Transactions',
    riskScore: 72
  }, {
    id: 'TX987656',
    merchantName: 'Metro Pharmacy',
    merchantId: 'M12347',
    customerName: 'Robert Williams',
    customerId: 'C54323',
    amount: 8750,
    date: '2023-10-28T08:15:00Z',
    paymentMethod: 'Mobile Wallet',
    status: 'completed',
    flagged: false,
    ruleTriggered: null,
    riskScore: null
  }, {
    id: 'TX987657',
    merchantName: 'Urban Clothing',
    merchantId: 'M12348',
    customerName: 'Emily Brown',
    customerId: 'C54324',
    amount: 35000,
    date: '2023-10-27T16:30:00Z',
    paymentMethod: 'Credit Card',
    status: 'pending',
    flagged: true,
    ruleTriggered: 'Large Transaction Amount',
    riskScore: 90
  }, {
    id: 'TX987658',
    merchantName: 'Downtown Cafe',
    merchantId: 'M12349',
    customerName: null,
    customerId: null,
    amount: 5000,
    date: '2023-10-27T14:45:00Z',
    paymentMethod: 'QR Code',
    status: 'failed',
    flagged: true,
    ruleTriggered: 'Multiple Failed Transactions',
    riskScore: 65
  }, {
    id: 'TX987659',
    merchantName: 'Tech Solutions',
    merchantId: 'M12350',
    customerName: 'Michael Davis',
    customerId: 'C54325',
    amount: 15000,
    date: '2023-10-27T13:15:00Z',
    paymentMethod: 'Bank Transfer',
    status: 'completed',
    flagged: false,
    ruleTriggered: null,
    riskScore: null
  }, {
    id: 'TX987660',
    merchantName: 'Fashion Outlet',
    merchantId: 'M12351',
    customerName: 'Jennifer Wilson',
    customerId: 'C54326',
    amount: 7500,
    date: '2023-10-27T11:30:00Z',
    paymentMethod: 'Credit Card',
    status: 'refunded',
    flagged: false,
    ruleTriggered: null,
    riskScore: null
  }];
  // Filter transactions based on search query, status filter, and flagged filter
  const filteredTransactions = transactionsData.filter(transaction => {
    const matchesSearch = transaction.merchantName.toLowerCase().includes(searchQuery.toLowerCase()) || transaction.id.toLowerCase().includes(searchQuery.toLowerCase()) || transaction.customerName && transaction.customerName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || transaction.status === statusFilter;
    const matchesFlagged = flaggedFilter === 'all' || flaggedFilter === 'flagged' && transaction.flagged || flaggedFilter === 'not-flagged' && !transaction.flagged;
    return matchesSearch && matchesStatus && matchesFlagged;
  });
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <StatusBadge status="success" text="Completed" data-id="element-6286" />;
      case 'pending':
        return <StatusBadge status="warning" text="Pending" data-id="element-6287" />;
      case 'failed':
        return <StatusBadge status="danger" text="Failed" data-id="element-6288" />;
      case 'refunded':
        return <StatusBadge status="info" text="Refunded" data-id="element-6289" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-6290" />;
    }
  };
  const getRiskBadge = (riskScore: number | null) => {
    if (riskScore === null) return null;
    if (riskScore >= 80) {
      return <StatusBadge status="danger" text={`High Risk (${riskScore})`} data-id="element-6291" />;
    } else if (riskScore >= 50) {
      return <StatusBadge status="warning" text={`Medium Risk (${riskScore})`} data-id="element-6292" />;
    } else {
      return <StatusBadge status="success" text={`Low Risk (${riskScore})`} data-id="element-6293" />;
    }
  };
  const handleViewDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowDetailModal(true);
  };
  const handleExport = (format: 'csv' | 'xlsx' | 'pdf') => {
    console.log(`Exporting transactions in ${format} format`);
    // In a real app, this would trigger the export functionality
  };
  return <div className="space-y-6" data-id="element-6294">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-6295">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-6296">
          Transaction Monitoring
        </h1>
        <div className="mt-3 sm:mt-0 flex items-center space-x-2" data-id="element-6297">
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6298">
            <CalendarIcon className="h-4 w-4 mr-2" data-id="element-6299" />
            Date Range
          </button>
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6300">
            <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-6301" />
            Refresh
          </button>
          <ExportMenu onExport={handleExport} data-id="element-6302" />
        </div>
      </div>
      <Card data-id="element-6303">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-3 md:space-y-0" data-id="element-6304">
          <div className="relative" data-id="element-6305">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-6306">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-6307" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:ring-blue-500 focus:border-blue-500" placeholder="Search by merchant, transaction ID, customer..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-6308" />
          </div>
          <div className="flex items-center space-x-4" data-id="element-6309">
            <div className="flex items-center space-x-2" data-id="element-6310">
              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-6311">
                Status:
              </span>
              <select className="block w-32 pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:ring-blue-500 focus:border-blue-500" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-6312">
                <option value="all" data-id="element-6313">All</option>
                <option value="completed" data-id="element-6314">Completed</option>
                <option value="pending" data-id="element-6315">Pending</option>
                <option value="failed" data-id="element-6316">Failed</option>
                <option value="refunded" data-id="element-6317">Refunded</option>
              </select>
            </div>
            <div className="flex items-center space-x-2" data-id="element-6318">
              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-6319">
                Flagged:
              </span>
              <select className="block w-32 pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:ring-blue-500 focus:border-blue-500" value={flaggedFilter} onChange={e => setFlaggedFilter(e.target.value)} data-id="element-6320">
                <option value="all" data-id="element-6321">All</option>
                <option value="flagged" data-id="element-6322">Flagged</option>
                <option value="not-flagged" data-id="element-6323">Not Flagged</option>
              </select>
            </div>
            <button className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6324">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-6325" />
              More Filters
            </button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-6326">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-6327">
            <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-6328">
              <tr data-id="element-6329">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-6330">
                  Transaction ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-6331">
                  Merchant
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-6332">
                  Customer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-6333">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-6334">
                  Date & Time
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-6335">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-6336">
                  Risk
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-6337">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-6338">
              {filteredTransactions.map(transaction => <tr key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-gray-750" data-id="element-6339">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-6340">
                    <div className="flex items-center" data-id="element-6341">
                      {transaction.flagged && <FlagIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-6342" />}
                      {transaction.id}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6343">
                    {transaction.merchantName}
                    <div className="text-xs text-gray-400 dark:text-gray-500" data-id="element-6344">
                      ID: {transaction.merchantId}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6345">
                    {transaction.customerName || 'Anonymous'}
                    {transaction.customerId && <div className="text-xs text-gray-400 dark:text-gray-500" data-id="element-6346">
                        ID: {transaction.customerId}
                      </div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-6347">
                    {formatCurrency(transaction.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6348">
                    {formatDate(transaction.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-6349">
                    {getStatusBadge(transaction.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-6350">
                    {transaction.flagged ? getRiskBadge(transaction.riskScore) : <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-6351">
                        -
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-6352">
                    <button onClick={() => handleViewDetails(transaction)} className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300" data-id="element-6353">
                      <EyeIcon className="h-5 w-5" data-id="element-6354" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {filteredTransactions.length === 0 && <div className="py-8 text-center" data-id="element-6355">
            <AlertTriangleIcon className="h-12 w-12 mx-auto text-gray-400" data-id="element-6356" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-6357">
              No transactions found
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-6358">
              {searchQuery || statusFilter !== 'all' || flaggedFilter !== 'all' ? "Try adjusting your search or filters to find what you're looking for." : 'There are no transactions to display at this time.'}
            </p>
          </div>}
      </Card>
      {/* Transaction Details Modal */}
      {selectedTransaction && <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)} title="Transaction Details" size="lg" footer={<button onClick={() => setShowDetailModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-6360">
              Close
            </button>} data-id="element-6359">
          <div className="space-y-6" data-id="element-6361">
            <div className="flex items-center justify-between" data-id="element-6362">
              <div data-id="element-6363">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-6364">
                  {selectedTransaction.id}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-6365">
                  {formatDate(selectedTransaction.date)}
                </p>
              </div>
              <div className="flex items-center space-x-2" data-id="element-6366">
                {getStatusBadge(selectedTransaction.status)}
                {selectedTransaction.flagged && getRiskBadge(selectedTransaction.riskScore)}
              </div>
            </div>
            {selectedTransaction.flagged && selectedTransaction.ruleTriggered && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4" data-id="element-6367">
                  <div className="flex" data-id="element-6368">
                    <AlertTriangleIcon className="h-5 w-5 text-red-400 mr-2" data-id="element-6369" />
                    <div data-id="element-6370">
                      <h3 className="text-sm font-medium text-red-800 dark:text-red-300" data-id="element-6371">
                        Flagged: {selectedTransaction.ruleTriggered}
                      </h3>
                      <p className="mt-1 text-sm text-red-700 dark:text-red-400" data-id="element-6372">
                        This transaction was flagged for review based on rule
                        violations.
                      </p>
                    </div>
                  </div>
                </div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6373">
              <div data-id="element-6374">
                <div className="flex items-center mb-2" data-id="element-6375">
                  <BuildingIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-6376" />
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-6377">
                    Merchant Information
                  </h4>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-3" data-id="element-6378">
                  <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-6379">
                    {selectedTransaction.merchantName}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-6380">
                    ID: {selectedTransaction.merchantId}
                  </p>
                </div>
              </div>
              <div data-id="element-6381">
                <div className="flex items-center mb-2" data-id="element-6382">
                  <UserIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-6383" />
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-6384">
                    Customer Information
                  </h4>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-3" data-id="element-6385">
                  <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-6386">
                    {selectedTransaction.customerName || 'Anonymous Customer'}
                  </p>
                  {selectedTransaction.customerId && <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-6387">
                      ID: {selectedTransaction.customerId}
                    </p>}
                </div>
              </div>
            </div>
            <div data-id="element-6388">
              <div className="flex items-center mb-2" data-id="element-6389">
                <CreditCardIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-6390" />
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-6391">
                  Transaction Details
                </h4>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-3" data-id="element-6392">
                <div className="grid grid-cols-2 gap-4" data-id="element-6393">
                  <div data-id="element-6394">
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-6395">
                      Amount
                    </p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-6396">
                      {formatCurrency(selectedTransaction.amount)}
                    </p>
                  </div>
                  <div data-id="element-6397">
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-6398">
                      Payment Method
                    </p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-6399">
                      {selectedTransaction.paymentMethod}
                    </p>
                  </div>
                  <div data-id="element-6400">
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-6401">
                      Date & Time
                    </p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-6402">
                      {formatDate(selectedTransaction.date)}
                    </p>
                  </div>
                  <div data-id="element-6403">
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-6404">
                      Status
                    </p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-6405">
                      {selectedTransaction.status.charAt(0).toUpperCase() + selectedTransaction.status.slice(1)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {selectedTransaction.flagged && <div data-id="element-6406">
                <div className="flex items-center justify-between mb-2" data-id="element-6407">
                  <div className="flex items-center" data-id="element-6408">
                    <AlertTriangleIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-6409" />
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-6410">
                      Risk Information
                    </h4>
                  </div>
                  <a href="#" className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center" data-id="element-6411">
                    View Alert Details
                    <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-6412" />
                  </a>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-3" data-id="element-6413">
                  <div className="grid grid-cols-2 gap-4" data-id="element-6414">
                    <div data-id="element-6415">
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-6416">
                        Risk Score
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-6417">
                        {selectedTransaction.riskScore} / 100
                      </p>
                    </div>
                    <div data-id="element-6418">
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-6419">
                        Rule Triggered
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-6420">
                        {selectedTransaction.ruleTriggered}
                      </p>
                    </div>
                  </div>
                </div>
              </div>}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-6421">
              <button className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center" data-id="element-6422">
                <DownloadIcon className="h-4 w-4 mr-1" data-id="element-6423" />
                Download Transaction Receipt
              </button>
            </div>
          </div>
        </Modal>}
    </div>;
};
export default TransactionMonitoring;